import { useState } from "react";
import { Mail, Phone, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Reveal, SectionHeading } from "./Reveal";
import { useT } from "../context/ContentContext";
import { useUI } from "../lib/i18n";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Button } from "./ui/button";

export default function ContactSection({ content }) {
  const t = useT();
  const ui = useUI();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success(ui.toast_ok);
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(ui.toast_err);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contatti" className="py-28 md:py-40 border-t border-white/5" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 md:gap-16">
        <div className="md:col-span-5">
          <SectionHeading index="04" label={ui.contact_label} title={t("contact_title")} testid="contact-title" />
          <Reveal delay={0.15}>
            <p className="mt-8 text-base md:text-lg text-zinc-400 leading-relaxed" data-testid="contact-subtitle">
              {t("contact_subtitle")}
            </p>
          </Reveal>
          <Reveal delay={0.25}>
            <div className="mt-10 space-y-5">
              <a href={`mailto:${content.contact_email}`} data-testid="contact-email-link"
                className="flex items-center gap-4 text-zinc-300 hover:text-[#007AFF] transition-colors group">
                <span className="w-11 h-11 rounded-full border border-white/15 flex items-center justify-center group-hover:border-[#007AFF] transition-colors">
                  <Mail className="w-4 h-4" />
                </span>
                <span className="text-sm md:text-base">{content.contact_email}</span>
              </a>
              <a href={`tel:${(content.contact_phone || "").replace(/[\s.]/g, "")}`} data-testid="contact-phone-link"
                className="flex items-center gap-4 text-zinc-300 hover:text-[#007AFF] transition-colors group">
                <span className="w-11 h-11 rounded-full border border-white/15 flex items-center justify-center group-hover:border-[#007AFF] transition-colors">
                  <Phone className="w-4 h-4" />
                </span>
                <span className="text-sm md:text-base">{content.contact_phone}</span>
              </a>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.2} className="md:col-span-7">
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="bg-white/[0.03] backdrop-blur-2xl border border-white/10 rounded-2xl p-8 md:p-10 space-y-6"
          >
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="contact-name" className="text-zinc-400">{ui.form_name}</Label>
                <Input
                  id="contact-name"
                  data-testid="contact-name-input"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder={ui.form_name_ph}
                  className="bg-black/40 border-white/15 focus-visible:ring-[#007AFF]"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact-email" className="text-zinc-400">Email</Label>
                <Input
                  id="contact-email"
                  type="email"
                  data-testid="contact-email-input"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder={ui.form_email_ph}
                  className="bg-black/40 border-white/15 focus-visible:ring-[#007AFF]"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact-message" className="text-zinc-400">{ui.form_message}</Label>
              <Textarea
                id="contact-message"
                data-testid="contact-message-input"
                required
                rows={6}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder={ui.form_message_ph}
                className="bg-black/40 border-white/15 focus-visible:ring-[#007AFF] resize-none"
              />
            </div>
            <Button
              type="submit"
              disabled={sending}
              data-testid="contact-submit-button"
              className="w-full sm:w-auto rounded-full bg-white text-black hover:bg-[#007AFF] hover:text-white px-8 py-6 text-sm font-semibold transition-colors"
            >
              {sending ? ui.form_sending : ui.form_send}
              <Send className="w-4 h-4 ml-2" />
            </Button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
